/**
 *
 * Carousel
 *
 */

import React, { useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import { Container, Div, FlexBox } from '../Blocks'
import media from '../../style/mediainjector'
import LazyLoadedMedia from '../LazyLoadedMedia'
import { getIsVideoFromUrl } from '../../utils/tools'
import VideoSlide from './VideoSlide'

export const ContainerCarousel = styled(Container)`
  padding: 0;
  ${media.mobile`
    padding-left: 40px;
    padding-right: 40px;
  `}
  ${media.lt667`
    padding-left: 40px;
    padding-right: 40px;
  `}
  ${media.lt480`
    padding-left: 20px;
    padding-right: 20px;
  `}
`

export const SlideButtons = styled.ul`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: space-between;
  width: 100%;
  margin: 32px auto;
  padding: 0;
  li {
    list-style: none;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-grow: 1;
    height: 36px;
    button {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-grow: 1;
      height: 36px;
      background-color: #fff;
      border: 1px solid #000;
      border-radius: 6px;
      color: #000;
    }
    &.opened {
      button {
        background-color: ${({ theme }) => theme.brand};
        color: #fff;
      }
    }
  }
  ${media.lt667`
    overflow-x: auto;
    margin: 0;
    button {
      min-width: 40%
    }
  `}
`

export const SlideWrap = styled(Div)`
  width: 100%;
  padding: 0;
  ${media.lt667`
    padding-top: 18px;
  `}
`

export const MediaBox = styled(FlexBox)`
  justify-content: space-between;
  align-items: flex-start;
  ${media.lt667`
    flex-direction: column;
  `}
`

export const SideBar = styled(Div)`
  max-width: 36%;
  padding-right: 18px;
  h4 {
    margin-top: 0;
  }
  ul {
    margin: 0;
    padding: 0;
    li {
      position: relative;
      display: flex;
      flex: 1;
      font-size: 1.6rem;
      list-style: none;
      min-width: 320px;
      padding-bottom: 1.8rem;
      &:last-child {
        padding-bottom: 0;
      }
      &:not(:last-child):after {
        content: '';
        position: absolute;
        left: 8px;
        top: 18px;
        width: 2px;
        height: 100%;
        background: ${({ theme, color }) => color || theme.brand};
      }
      &::before {
        content: '';
        display: block;
        width: 18px;
        height: 18px;
        flex-shrink: 0;
        margin-right: 18px;
        border-radius: 100%;
        background: ${({ theme, color }) => color || theme.brand};
      }
    }
  }
  ${media.lt667`
    order: 2;
    h4 {
      display: none;
    }
    ul {
      padding: 0 18px;
      li {
        min-width: 280px;
      }
    }
  `}
`

export const MediaSourceBox = styled(Div)`
  width: 100%;
`

export const Image = styled.img`
  width: 100%;
  display: block;
  margin: 0 auto;
`

const Carousel = ({ elencoMaterie }) => {
  const [sectionOpened, setSectionOpened] = useState(0)

  return (
    <ContainerCarousel>
      <SlideButtons>
        {elencoMaterie.map(({ titoloArgomento, homePageUrlArgomento }, k) => (
          <li
            key={`titolo_argomento_${k + 1}`}
            className={
              sectionOpened === k || !!homePageUrlArgomento ? 'opened' : ''
            }
          >
            <button
              key={`button_argomento_${k + 1}`}
              type="button"
              onClick={
                sectionOpened === k && !homePageUrlArgomento
                  ? null
                  : () => {
                      if (!homePageUrlArgomento) {
                        setSectionOpened(k)
                      } else {
                        window.open(homePageUrlArgomento)
                      }
                    }
              }
            >
              {titoloArgomento}
            </button>
          </li>
        ))}
      </SlideButtons>
      <LazyLoadedMedia>
        {elencoMaterie.map(
          (
            {
              titoloArgomento,
              contenutoArgomento,
              immagineFallbackArgomento,
              videoArgomentoUrl,
              immagineArgomento,
            },
            k
          ) => {
            if (!contenutoArgomento) {
              return null
            }

            const videoUrl = videoArgomentoUrl || immagineArgomento?.url
            const isVideo = getIsVideoFromUrl(videoUrl)

            return (
              <SlideWrap
                key={`blocco_aperto_${k + 1}`}
                style={{ display: sectionOpened === k ? 'block' : 'none' }}
              >
                <MediaBox>
                  <SideBar>
                    <h4>{titoloArgomento}</h4>
                    <Div
                      dangerouslySetInnerHTML={{ __html: contenutoArgomento }}
                    />
                  </SideBar>
                  {!!videoUrl && (
                    <MediaSourceBox>
                      {isVideo ? (
                        <VideoSlide
                          visible={sectionOpened === k}
                          videoUrl={videoUrl}
                          immagineFallbackArgomento={immagineFallbackArgomento}
                        />
                      ) : (
                        <Image src={videoUrl} />
                      )}
                    </MediaSourceBox>
                  )}
                </MediaBox>
              </SlideWrap>
            )
          }
        )}
      </LazyLoadedMedia>
    </ContainerCarousel>
  )
}

Carousel.propTypes = {
  elencoMaterie: PropTypes.arrayOf(
    PropTypes.shape({
      titoloArgomento: PropTypes.string.isRequired,
      contenutoArgomento: PropTypes.string,
      immagineArgomento: PropTypes.shape({
        url: PropTypes.string.isRequired,
      }),
      videoArgomentoUrl: PropTypes.string,
      immagineFallbackArgomento: PropTypes.object,
      immagineFallbackArgomento: PropTypes.object,
      homePageUrlArgomento: PropTypes.string,
    })
  ),
}

export default Carousel
